import Feature from './feature';
import Dialog from './dialog';
import MapMenu from './map_menu';
import Utils from './utils';

/**
 * The main interaction layer handling click on map features
 */
const UiInteraction = {
	_replacingPopup: false,

	click: function (mapboxFeature) {
		const feature = Feature.factory(mapboxFeature.properties);

		// Don't open popup if it's already open
		if (W.popups[0]?.key === feature.key) {
			return;
		}

		// "feature" here is our JS instance model synced with our server API
		// "mapboxFeature" is the Mapbox geojson feature we clicked on
		this.fetchAndAddFeature(feature, { mapboxFeature: mapboxFeature });
	},

	fetchAndAddFeature: function (feature, opts = {}) {
		if (undefined === opts.mapboxFeature) {
			opts.mapboxFeature = {};
		}

		// fetch our model's data and return the model
		feature.fetch().then((model) => {
			let object_type = model.object_type ? model.object_type : model.data.object_type;

			// allows us to distinguish between tag layer popups and
			// regular building popups
			if (opts.mapboxFeature?.layer?.id.startsWith('tag-layer-')) {
				object_type = 'tag';
			}
			// const offset = ['bus-stops', 'building', 'parking_lot', 'natrual_area', 'recreational_area'].includes(object_type) ? [0, 0] : [0, -35];

			// for GA4 tracking
			let gaValue = 'BUILDING: ' + model.data.name;
			if (object_type === 'parking_lot') {
				gaValue = 'PARKING: Lot ' + model.data.building_number;
			} else if (object_type === 'dining') {
				gaValue = 'DINING: ' + model.data.name;
			} else if (object_type === 'gender_neutral_restrooms') {
				gaValue = 'BATHROOM: ' + model.data.map_object.name;
			} else if (object_type === 'library') {
				gaValue = 'LIBRARY: ' + model.data.name;
			} else if (object_type === 'bus_stops') {
				gaValue = 'BUS STOP: ' + model.data.name;
			} else if (object_type === 'tag') {
				// currently, only one tag layer so we know it's housnig
				gaValue = 'HOUSING: ' + model.data.name;
			}
			if (opts?.origin === 'search') {
				gaValue = 'SEARCH: ' + gaValue;
			}

			// AnimatedPopup is an extension of the standard MapboxGL Popup
			const popup = new AnimatedPopup({
				anchor: 'bottom',
				className: 'uw-popover uw-popover-' + object_type,
				closeOnClick: false,
				focusAfterOpen: true,
				openingAnimation: {
					duration: 500,
					easing: 'easeOutBack',
					transform: 'scale',
				},
				closingAnimation: {
					duration: 250,
					easing: 'easeInBack',
					transform: 'scale',
				},
			})
				.setLngLat(model.data.lnglat)
				.setHTML(model.popupContent())
				.setMaxWidth('350px')
				.on('open', (e) => {
					const popupDialogLink = e.target._content.querySelector('button[data-dialog-url]');
					console.log(popupDialogLink);
					if (popupDialogLink) {
						popupDialogLink.addEventListener('click', this.handleDialogLinkClick);
					}
					if (object_type === 'bcycle') {
						model.displayRealtime();
					}
					if (typeof gtag !== 'undefined') {
						gtag('event', 'open-popup', { event_category: 'Popup', event_label: gaValue });
					}
					MapMenu.hide();
				});

			UiInteraction._replacingPopup = W.popups.length > 0;
			UiInteraction.clearAllPopups();
			// Track open popup
			// (we can only have one popup open at a time; if this
			// changes, we'll need to change the way we handle)
			W.popups = [{ key: model.key }];
			popup.addTo(W.map);

			// Mapbox incorrectly adds aria-hidden to the close button; fix that
			popup.getElement().querySelector('.mapboxgl-popup-close-button').removeAttribute('aria-hidden');

			// add accessibility support
			popup.getElement().setAttribute('role', 'dialog');
			popup.getElement().setAttribute('tabindex', '-1');
			popup.getElement().setAttribute('aria-labelledby', 'popup-name');

			// Focus the popup name when replacing an existing popup
			// const popupName = popup.getElement().querySelector('#popup-name');
			// if (popupName) {
			// 	popupName.focus();
			// }

			// If viewport is "short" or "small" (ie phone), shift panTo point down 100px
			let offset = [0, 50];
			if (W.resp.short() || W.resp.small()) {
				offset = [0, 100];
			}

			W.map.panTo(popup.getLngLat(), { duration: 800, offset: offset }); // TODO: do we want to do this?

			// Highlight the clicked map object, AFTER panning to it
			// Features from vector layers will have a mapboxFeature object
			// with an id attibute. Features from our locally-added Geojson
			// layers or from the search results will not but we can query
			// it from the rendered mapbox tiles, but we need to wait a bit
			// for the tiles to render (TODO: is there an async way to handle this?)
			if (undefined === opts.mapboxFeature.id) {
				setTimeout(() => {
					opts = this.queryFeature(model, opts);
					this.toggleMapStyleFeatureState(opts.mapboxFeature, 'highlighted');
				}, 250);
			} else {
				this.toggleMapStyleFeatureState(opts.mapboxFeature, 'highlighted');
			}

			popup.on('close', (e) => {
				const popupKey = e.target._content.querySelector('.content-wrapper').dataset.key;
				const popupDialogLink = e.target._content.querySelector('button[data-dialog-url]');

				if (popupDialogLink) {
					popupDialogLink.removeEventListener('click', this.handleDialogLinkClick);
				}

				this.clearPopup(popupKey);

				// Return focus to search only when popup is dismissed,
				// not when it's being replaced by another popup
				if (!UiInteraction._replacingPopup && !Utils.hasTouch()) {
					document.getElementById('search_input').focus();
				}
				UiInteraction._replacingPopup = false;

				// when popup closes, toggle the highlighted state too
				if (opts.mapboxFeature.id) {
					this.toggleMapStyleFeatureState(opts.mapboxFeature, 'highlighted');
				}
			});
		});
	},

	// get the map_object_id form our model instance and then use that
	// to query the rendered vector layer for the feature using
	// querySourceFeatures() with a filter for the id value.
	queryFeature: function (model, opts) {
		const map_object_id = model.data.map_object_id;
		let sourceLayerId = 'uw_buildings';
		const object_type = model.data.object_type;
		if (object_type === 'natural_area') {
			sourceLayerId = 'uw_natural_areas';
		} else if (object_type === 'parking_lot') {
			sourceLayerId = 'uw_parking_lots';
		}

		if (map_object_id && sourceLayerId) {
			const mapObjectFeature = W.map.querySourceFeatures('composite', {
				sourceLayer: sourceLayerId,
				filter: ['==', ['get', 'map_object_id'], map_object_id],
			});
			opts.mapboxFeature.id = mapObjectFeature[0]?.id; // only need 1
			opts.mapboxFeature.source = 'composite';
			opts.mapboxFeature.sourceLayer = sourceLayerId;
		}
		return opts;
	},

	toggleMapStyleFeatureState: function (feature, state) {
		if (undefined === feature.id) {
			return false;
		}

		const mapboxFeatureParams = {
			id: feature.id,
			source: feature.source,
			sourceLayer: feature.sourceLayer,
		};

		// toggle the state
		let newState = !W.map.getFeatureState(mapboxFeatureParams).highlighted;

		W.map.setFeatureState(mapboxFeatureParams, {
			[state]: newState,
		});
	},

	handleDialogLinkClick: (e) => {
		e.preventDefault();
		UiInteraction.dialogLink = e.target.closest('button');
		const url = UiInteraction.dialogLink.dataset.dialogUrl;

		const photoUrl = UiInteraction.dialogLink.dataset.photoUrl;
		// preload the image
		if (photoUrl) {
			const img = new Image();
			img.src = photoUrl;
		}

		let dialogType;

		if (url.includes('map_objects')) {
			dialogType = 'mapObject';
		} else if (url.includes('bus_stops')) {
			dialogType = 'busStop';
		}

		// Open the dialog and post process as needed
		Dialog.load(url).then((dialog) => {
			if (dialogType === 'mapObject') {
				UiInteraction.postProcessMapObjectDialog(dialog);
			}
			if (dialogType === 'busStop') {
				UiInteraction.postProcessBusStopDialog(dialog);
			}
		});
	},

	postProcessMapObjectDialog: async (dialog) => {
		dialog.setAttribute('aria-labelledby', 'dialog-label');
		dialog.style.setProperty('--width', '500px');
		const tabGroups = dialog.querySelector('sl-tab-group');
		let sectionId;

		// Wait for Shoelace sl-tab-group Web Component to update
		await tabGroups.updateComplete;

		if (UiInteraction.dialogLink.dataset.dialogDining) {
			sectionId = 'details_dining_' + UiInteraction.dialogLink.dataset.dialogDining;
			tabGroups.show('dining');
		}
		if (UiInteraction.dialogLink.dataset.dialogLibraries) {
			sectionId = 'details_library_' + UiInteraction.dialogLink.dataset.dialogLibraries;
			tabGroups.show('libraries');
		}

		if (sectionId) {
			// Open and scroll to the appropriate details section
			const sectionTarget = document.getElementById(sectionId);
			// Wait for Shoelace sl-details Web Component to update
			await sectionTarget.updateComplete;
			sectionTarget.open = true;
			sectionTarget.scrollIntoView({ block: 'start', behavior: 'smooth' });
		}

		// close all details except clicked one (which fires sl-show event)
		const details_container = document.querySelector('.uw-details-group');
		if (details_container) {
			details_container.addEventListener('sl-show', (event) => {
				if (event.target.localName === 'sl-details') {
					[...details_container.querySelectorAll('sl-details')].map(
						(details) => (details.open = event.target === details),
					);
				}
			});
		}

		// track Departments tab clicks
		const departmentsTab = dialog.querySelector('sl-tab[panel="departments"]');
		if (departmentsTab) {
			departmentsTab.addEventListener('click', (e) => {
				const name = e.target.dataset.departmentTab;
				if (typeof gtag !== 'undefined') {
					gtag('event', 'click', { event_category: 'DepartmentTab', event_label: name });
				}
			});
		}
	},

	postProcessBusStopDialog: (dialog) => {
		dialog.setAttribute('aria-labelledby', 'dialog-label');
		const stop_id = document.getElementById('stop_view').dataset.stopId;
		const url = W.api_root + 'bus_stops/' + stop_id + '/realtime.html';
		const error_message = '<p style="color:#c5050c">Unable to retrieve data at this time.</p>';
		const loading = document.querySelector('#stop_realtime .loading');

		const loadRealtime = () => {
			const liveDataEl = document.querySelector('#stop_realtime .live-data');

			liveDataEl.innerHTML = '';

			fetch(url)
				.then((response) => {
					if (!response.ok) {
						loading.style.display = 'none';
						liveDataEl.innerHTML = error_message;
						throw new Error(`HTTP error! Status: ${response.status}`);
					}
					return response.text();
				})
				.then((stop_realtime_html) => {
					loading.style.display = 'none';
					liveDataEl.innerHTML = stop_realtime_html;
					liveDataEl.style.display = 'block';
				});
		};
		loadRealtime();

		// Stop realtime reload trigger
		const reloadRealtimeButton = document.getElementById('reloadRealtime');
		reloadRealtimeButton.addEventListener('click', (e) => {
			e.preventDefault();
			loading.style.display = 'block';
			loadRealtime();
		});
	},

	clearPopup: (key) => {
		W.popups = W.popups.filter((p) => p.key !== key);
	},

	clearAllPopups: () => {
		W.map._popups.forEach((popup) => popup.remove());
		W.popups = [];
	},
};

export default UiInteraction;
